import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import notify, { AppToastContainer } from "../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import ascendLogo from "../../../assets/ACS-Images/Ascend-Logo.png";
import OnboardingImg from "../../../assets/ACS-Images/Onboarding_Img.jpg";

const AboutYou = () => {
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        companyName: "",
        officeCode: "",
        jobTitle: "",
        officePhone: "",
        industry: "",
        previousSystem: "",
        teamSize: ""
    });

    const [companyNameError, setCompanyNameError] = useState("");
    const [isScacLocked, setIsScacLocked] = useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const code = searchParams.get("code");

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === "companyName") {
            if (/[^a-zA-Z0-9 ]/.test(value)) {
                setCompanyNameError("Special characters are not allowed.");
            } else {
                setCompanyNameError("");
                setFormData((prev) => ({ ...prev, [name]: value }));
            }

            return;
        }
        else if (name === "officeCode" && formData.officeCode != null) {
            formData.officeCode = formData.officeCode.toUpperCase();
             setFormData((prev) => ({ ...prev, [name]: value.toUpperCase() }));
             return;
        }

        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const validateForm = () => {
        const regex = /^[a-zA-Z0-9 ]+$/;
        const requiredFields = ["firstName", "companyName", "officeCode", "jobTitle", "officePhone", "teamSize"];
        const missing = requiredFields.filter((f) => !formData[f]);
        if (missing.length > 0) {
            toast.error(`Please fill in all required fields: ${missing.join(", ")}`);
            return false;
        }
        if (formData.companyName && !regex.test(formData.companyName)) {
            toast.error("Company Name can only contain letters, numbers, and spaces.");
            return false;
        }
        return true;
    };

    useEffect(() => {
        const fetchEmail = async () => {
            if (!code) return;
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_API_BASE_URL}/api/invites/search`,
                    {
                        params: { inviteCode: code },
                        headers: { "Content-Type": "application/json" }
                    }
                );
                if (response.data?.recipientEmail) {
                    setFormData(prev => ({ ...prev, email: response.data.recipientEmail }));
                }
                if (response.data?.scacCode) {
                    setFormData(prev => ({ ...prev, officeCode: response.data.scacCode }));
                    setIsScacLocked(true);
                }

            } catch (error) {
                console.error("Error fetching invite data:", error);
            }
        };
        fetchEmail();
    }, [code]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setLoading(true);
        const trimmedFormData = Object.fromEntries(
            Object.entries(formData).map(([key, value]) =>
                ["firstName", "lastName", "officePhone"].includes(key) && typeof value === "string"
                    ? [key, value.trim()]
                    : [key, value]
            )
        );
        try {
            await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/onboarding/user-profile`,
                trimmedFormData,
                {
                    params: { inviteCode: code },
                    headers: { "Content-Type": "application/json" }
                }
            );

            navigate("/onboarding/company-info", {
                state: { companyName: formData.companyName, email: formData.email }
            });
        } catch (error) {
            if (error.response?.status === 400) {
                error.response.data.content.split(",").forEach(msg => notify.error({
                    title: "Failed to save Profile",
                    message: msg.trim()
                }));
            } else {
                const errorMessage = error.response?.data?.content || "Failed to save profile data";

                notify.error({ title: "Failed to save Profile", message: errorMessage });
                // notify.error(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="w-full min-h-screen bg-white grid grid-cols-1 md:grid-cols-12">
            {/* Left Picture (25%) */}
            <div
                className="hidden md:block md:col-span-2 h-screen bg-cover bg-center sticky top-0"
                style={{ backgroundImage: `url(${OnboardingImg})` }}
                aria-hidden="true"
            />

            {/* Center Form (50%) */}
            <div className="md:col-span-8 min-h-screen flex items-start md:items-center justify-center py-8 md:py-12">
                <form onSubmit={handleSubmit} className="w-full max-w-7xl px-4 md:px-10 py-4 md:py-10 space-y-fluid-3">
                    {/* Logo + Steps */}
                    <div className="w-full max-w-5xl px-0 md:px-8 py-0 md:py-6 mx-auto">
                        <div className="flex flex-col items-center space-y-4">
                            <img
                                src={ascendLogo}
                                alt="Ascend Logo"
                                className="w-32 md:w-40 mb-2 md:mb-6 hover:scale-105 transition-transform duration-300"
                            />
                            <div className="flex items-center justify-center gap-6 mt-2 mb-4">
                                {["About yourself", "Company Information", "Invite your team"].map((label, idx) => {
                                    const isActive = idx === 0;
                                    return (
                                        <React.Fragment key={idx}>
                                            {idx > 0 && <div className="w-8 border-t-2 border-gray-300" />}
                                            <div className="flex flex-col items-center">
                                                <div
                                                    className={`w-8 h-8 rounded-full font-semibold flex items-center justify-center
                          ${isActive ? "border-2 border-[#223688] bg-[#e8edff] text-[#223688]" : "border border-gray-300 bg-white text-gray-400"}`}
                                                >
                                                    {idx + 1}
                                                </div>
                                                <span
                                                    className={`mt-1 text-sm font-semibold text-center ${isActive ? "text-black" : "text-gray-400"}`}>
                                                    {label}
                                                </span>
                                            </div>
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Title */}
                    <div className="text-center mb-fluid-4">
                        <h1 className="text-fluid-xl font-bold">Tell us about yourself</h1>
                    </div>

                    {/* Form Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-fluid-2">
                        <div>
                            <label className="block text-sm font-medium mb-1">
                                First name<span className="text-red-600">*</span>
                            </label>
                            <input
                                type="text"
                                name="firstName"
                                className="w-full border border-gray-300 rounded-lg px-4 py-2"
                                value={formData.firstName}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">
                                Last name<span className="text-red-600">*</span>
                            </label>
                            <input
                                type="text"
                                name="lastName"
                                className="w-full border border-gray-300 rounded-lg px-4 py-2"
                                value={formData.lastName}
                                onChange={handleChange}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">
                                Email<span className="text-red-600">*</span>
                            </label>
                            <input
                                type="email"
                                name="email"
                                className="w-full border border-gray-300 rounded-lg px-4 py-2"
                                value={formData.email}
                                onChange={handleChange}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">
                                Company Name<span className="text-red-600">*</span>
                            </label>
                            <input
                                type="text"
                                name="companyName"
                                className="w-full border border-gray-300 rounded-lg px-4 py-2"
                                value={formData.companyName}
                                onChange={handleChange}
                                required
                            />
                            {companyNameError && (
                                <p className="text-red-500 text-xs mt-1">{companyNameError}</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">
                                Office Code/SCAC Code<span className="text-red-600">*</span>
                            </label>
                            <input
                                type="text"
                                name="officeCode"
                                className={`w-full border border-gray-300 rounded-lg px-4 py-2 ${isScacLocked ? "bg-gray-100 cursor-not-allowed" : ""}`}
                                value={formData.officeCode}
                                onChange={handleChange}
                                readOnly={isScacLocked}
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">
                                Job title<span className="text-red-600">*</span>
                            </label>
                            <select
                                name="jobTitle"
                                className="w-full border border-gray-300 rounded-lg px-4 py-2"
                                value={formData.jobTitle}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select a title</option>
                                <option value="Manager">Manager</option>
                                <option value="Logistics Coordinator">Logistics Coordinator</option>
                                <option value="Operations Head">Operations Head</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">
                                Office phone number<span className="text-red-600">*</span>
                            </label>
                            <input
                                type="tel"
                                name="officePhone"
                                className="w-full border border-gray-300 rounded-lg px-4 py-2"
                                value={formData.officePhone}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">Industry<span className="text-red-600">*</span></label>
                            <select
                                name="industry"
                                className="w-full border border-gray-300 rounded-lg px-4 py-2"
                                value={formData.industry}
                                onChange={handleChange}
                            >
                                <option value="">Select an industry</option>
                                <option value="Retail">Retail</option>
                                <option value="Manufacturing">Manufacturing</option>
                                <option value="Logistics">Logistics</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">What system did you use before?<span className="text-red-600">*</span></label>
                            <select
                                name="previousSystem"
                                className="w-full border border-gray-300 rounded-lg px-4 py-2"
                                value={formData.previousSystem}
                                onChange={handleChange}
                            >
                                <option value="">Select all that apply</option>
                                <option value="SAP">SAP</option>
                                <option value="Oracle">Oracle</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">
                                How many team members do you have?<span className="text-red-600">*</span>
                            </label>
                            <select
                                name="teamSize"
                                className="w-full border border-gray-300 rounded-lg px-4 py-2"
                                value={formData.teamSize}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select a number</option>
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={21}>21+</option>
                            </select>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-center gap-6 mt-fluid-4">
                        <button
                            type="submit"
                            className="w-40 px-6 py-2 bg-[#223688] text-white rounded-full hover:bg-[#1c2c6d] transition"
                            disabled={loading}
                        >
                            {loading ? "Processing.." : "Continue"}
                        </button>
                    </div>
                </form>

                <ToastContainer position="top-right" autoClose={5000} />
            </div>

            {/* Right Picture (25%) */}
            <div
                className="hidden md:block md:col-span-2 h-screen bg-cover bg-center sticky top-0"
                style={{ backgroundImage: `url(${OnboardingImg})` }}
                aria-hidden="true"
            />
        </div>
    );
};

export default AboutYou;
